import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { colors, spacing, shadows } from '../theme/index';
import { useAuth } from '../context/AuthContext';
import API_BASE_URL from '../config/api';

function MyOrders() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchOrders();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchOrders = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/orders/my-orders`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();
      setOrders(data.data || []);
    } catch (err) {
      console.error('Error fetching orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'customer_paid':
        return { label: '⏳ Payment Received - Verifying', color: colors.accent[500], bg: colors.accent[50] };
      case 'confirmed':
      case 'processing':
        return { label: '✅ Confirmed', color: colors.success, bg: '#f0fdf4' };
      case 'shipped':
        return { label: '🚚 Shipped', color: colors.primary[600], bg: colors.primary[25] };
      case 'delivered':
        return { label: '📦 Delivered', color: colors.success, bg: '#f0fdf4' };
      case 'payment_failed':
        return { label: '❌ Payment Failed', color: colors.error, bg: '#fef2f2' };
      default:
        return { label: status || 'Processing', color: colors.text.secondary, bg: colors.neutral[100] };
    }
  };

  if (!token) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: colors.background, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: spacing.xl }}>
        <motion.div style={{ textAlign: 'center', backgroundColor: 'white', borderRadius: '1rem', padding: spacing.xl, boxShadow: shadows.md }} initial={{ scale: 0.9 }} animate={{ scale: 1 }}>
          <div style={{ fontSize: '4rem', marginBottom: spacing.lg }}>🔐</div>
          <p style={{ fontSize: '1.25rem', fontWeight: 700, color: colors.text.primary, marginBottom: spacing.md }}>Please login to view your orders</p>
          <motion.button onClick={() => navigate('/user-login')} style={{ backgroundColor: colors.primary[600], color: 'white', padding: `${spacing.md} ${spacing.xl}`, border: 'none', borderRadius: '0.5rem', fontWeight: 700, cursor: 'pointer', fontSize: '1rem' }} whileHover={{ backgroundColor: colors.primary[700] }} whileTap={{ scale: 0.95 }}>
            Go to Login
          </motion.button>
        </motion.div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: colors.background, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <motion.div animate={{ scale: [1, 1.1, 1] }} transition={{ duration: 1.5, repeat: Infinity }} className="text-center">
          <div style={{ fontSize: '4rem', marginBottom: spacing.lg }}>📦</div>
          <p style={{ fontSize: '1.25rem', fontWeight: 700, color: colors.text.primary }}>Loading your orders...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.background }}>
      {/* Header */}
      <motion.div style={{ background: `linear-gradient(135deg, ${colors.primary[600]} 0%, ${colors.primary[700]} 100%)`, color: 'white', padding: `${spacing.xl} ${spacing.lg}` }} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <div style={{ maxWidth: '80rem', margin: '0 auto' }}>
          <motion.h1 style={{ fontSize: '2rem', fontWeight: 900, margin: 0 }} initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
            📋 My Orders
          </motion.h1>
          <p style={{ margin: `${spacing.sm} 0 0`, opacity: 0.9 }}>{orders.length} order{orders.length !== 1 ? 's' : ''} placed</p>
        </div>
      </motion.div>

      <div style={{ maxWidth: '80rem', margin: '0 auto', padding: spacing.xl }}>
        {orders.length === 0 ? (
          <motion.div style={{ textAlign: 'center', backgroundColor: 'white', borderRadius: '1rem', padding: spacing.xl, boxShadow: shadows.md }} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div style={{ fontSize: '4rem', marginBottom: spacing.lg }}>🛍️</div>
            <p style={{ fontSize: '1.25rem', fontWeight: 700, color: colors.text.primary, marginBottom: spacing.md }}>No orders yet</p>
            <p style={{ color: colors.text.secondary, marginBottom: spacing.lg }}>Start shopping to place your first order!</p>
            <motion.button onClick={() => navigate('/products')} style={{ backgroundColor: colors.primary[600], color: 'white', padding: `${spacing.md} ${spacing.xl}`, border: 'none', borderRadius: '0.5rem', fontWeight: 700, cursor: 'pointer', fontSize: '1rem' }} whileHover={{ backgroundColor: colors.primary[700] }} whileTap={{ scale: 0.95 }}>
              🛒 Browse Products
            </motion.button>
          </motion.div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.lg }}>
            {orders.map((order, idx) => {
              const badge = getStatusBadge(order.status);
              const estimatedDate = order.estimatedDelivery ? new Date(order.estimatedDelivery) : null;
              const formattedDate = estimatedDate ? estimatedDate.toLocaleDateString('en-IN', { weekday: 'short', month: 'short', day: 'numeric' }) : null;
              
              return (
                <motion.div
                  key={order.id}
                  style={{ backgroundColor: 'white', borderRadius: '1rem', padding: spacing.lg, boxShadow: shadows.md, borderLeft: `6px solid ${badge.color}` }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  whileHover={{ y: -2 }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: spacing.md }}>
                    <div>
                      <p style={{ margin: 0, fontSize: '0.85rem', color: colors.text.secondary, fontWeight: 600 }}>ORDER #{order.id}</p>
                      <p style={{ margin: `${spacing.xs} 0 0`, fontSize: '0.85rem', color: colors.text.secondary }}>
                        📅 {order.date ? new Date(order.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A'}
                      </p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{ display: 'inline-block', padding: `${spacing.xs} ${spacing.md}`, borderRadius: '9999px', fontSize: '0.85rem', fontWeight: 700, backgroundColor: badge.bg, color: badge.color }}>
                        {badge.label}
                      </span>
                      <p style={{ margin: `${spacing.sm} 0 0`, fontSize: '1.25rem', fontWeight: 900, color: colors.primary[600] }}>₹{Math.round(order.totalAmount || order.amount || 0)}</p>
                    </div>
                  </div>

                  {/* Items */}
                  {order.items && order.items.length > 0 && (
                    <div style={{ paddingTop: spacing.md, borderTop: `1px solid ${colors.border}` }}>
                      <p style={{ margin: `0 0 ${spacing.sm}`, fontSize: '0.85rem', fontWeight: 700, color: colors.text.secondary }}>Items:</p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
                        {order.items.map((item, i) => (
                          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                            <span style={{ color: colors.text.primary }}>
                              {item.name} × {item.quantity}
                              {item.side && (
                                <span style={{ fontSize: '0.8rem', color: colors.text.secondary, marginLeft: '0.5rem' }}>
                                  ({item.side === 'right' ? 'Right' : 'Left'} side)
                                </span>
                              )}
                            </span>
                            <span style={{ fontWeight: 700, color: colors.primary[600] }}>₹{Math.round(item.price * item.quantity)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Confirmed orders - show delivery ETA */}
                  {(order.status === 'confirmed' || order.status === 'processing' || order.status === 'shipped') && formattedDate && (
                    <div style={{ marginTop: spacing.md, padding: `${spacing.sm} ${spacing.md}`, backgroundColor: '#f0fdf4', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', gap: spacing.sm }}>
                      <span style={{ fontSize: '1.25rem' }}>🚚</span>
                      <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#15803d' }}>Estimated delivery: {formattedDate}</span>
                    </div>
                  )}

                  {/* Customer paid - show verification in progress */}
                  {order.status === 'customer_paid' && (
                    <div style={{ marginTop: spacing.md, padding: `${spacing.sm} ${spacing.md}`, backgroundColor: colors.accent[50], borderRadius: '0.5rem', display: 'flex', alignItems: 'center', gap: spacing.sm }}>
                      <span style={{ fontSize: '1.25rem' }}>⏳</span>
                      <span style={{ fontSize: '0.85rem', fontWeight: 600, color: colors.accent[600] }}>Your payment is being verified by our team</span>
                    </div>
                  )}

                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default MyOrders;